webpackJsonp([44],{

/***/ "./node_modules/json-loader/index.js!./.cache/json/advanced-2-forms-modelling.json":
/***/ (function(module, exports) {

	module.exports = {
		"data": {
			"markdownRemark": {
				"html": "<p>Hypermedia benefits from affordances. These are hints from the server about what the client is able to ask the server to do. The most underutilised affordance is forms. Forms are templates and in practice are based on HTML forms. In the context of a resource, a form provides message framing details about how to send information back to the server and in what form.</p>\n<p>Forms can also provide information to the client on how to display the information when there is human input required.</p>\n<p><img src=\"/hypermedia/advanced/form-modelling.png\" alt=\"form modelling\"></p>\n<p>There are a number of situations that use forms:</p>\n<ul>\n<li>create a resource on a collection</li>\n<li>update a resource</li>\n<li>search (which is to create a resource on a collection)</li>\n<li>change membership in a collection</li>\n</ul>\n<Instruction>\n<p>Semantic Link has these three top-level pre-baked forms</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash.\"  path=\"...todo-hypermedia/api/SemanticLink/Form\"  nocopy=\"false\"><code>└── Form\n    ├── CreateFormRepresentation\n    ├── EditFormRepresentation\n    └── SearchFormRepresentation</code></pre>\n      </div>\n</Instruction>\n<p>When modelling hypermedia, resources are modelled as nouns. In these tutorials, forms are usually modelled as links through verbs (eg pay, payment, search) or through the ubiquitious edit-form or create-form. This is in contrast to some microformats that embed the forms in the resource itself (eg HAL-FORMS as <code>_templates</code> attribute).</p>\n<p>Finally, the implementation of forms in Semantic Link is based on the collection abstraction. A form representation is a linked representation and, like a collection, will have <code>items</code>.</p>\n<h3>Heads up! forms need to cater for</h3>\n<p>HTML forms model most aspects of a hypermedia form:</p>\n<ul>\n<li>field: type, name, value, validations (eg text, password, email, number, url, tel, date, time, range, color—see <a href=\"https://www.w3.org/TR/html5/single-page.html#the-input-element\">html spec</a>)</li>\n<li>single (by value)</li>\n<li>enumeration (by value) (eg select)</li>\n</ul>\n<Instruction>\n<p>Here are the main types of fields found:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash.\"  path=\"...todo-hypermedia/api/SemanticLink/Form\"  nocopy=\"false\"><code>.\n└── Form\n    ├── CheckInputFormItemRepresentation\n    ├── DateInputFormItemRepresentation\n    ├── DateTimeInputFormItemRepresentation\n    ├── EmailInputFormItemRepresentation\n    ├── PasswordInputFormItemRepresentation\n    ├── SelectFormItemRepresentation\n    ├── SelectOptionItemRepresentation\n    ├── TextInputFormItemRepresentation\n    └── UriInputFormItemRepresentation</code></pre>\n      </div>\n</Instruction>\n<p>But HTML also needs a bit more modelling:</p>\n<ul>\n<li>groups</li>\n<li>values/enumerations by reference (that is not inline but found somewhere else via link relations)</li>\n</ul>\n<Instruction>\n<p>Here are the main types of fields found:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash.\"  path=\"...todo-hypermedia/api/SemanticLink/Form\"  nocopy=\"false\"><code>.\n└── Form\n    ├── CollectionInputFormItemRepresentation\n    ├── GroupFormItemRepresentation\n    ├── JsonPointerInputFormItemRepresentation\n    ├── SelectOptionGroupItemRepresentation\n    ├── SelectOptionItemRepresentation\n    └── SelectOptionValueItemRepresentation</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>In pseudo code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-uml\"   nocopy=\"false\"><code>abstract class LinkedRepresentation {\n    Links : WebLink[]\n}\n\nclass WebLink {\n   Rel : string\n   HRef : string\n   Title : string?\n   Type : string?\n}\n\nclass FormRepresentation : LinkedRepresentation {\n    Items : FormItemRepresentation[]\n}\n\nabstract class FormItemRepresentation  {\n    // NOTE: clients bind local controls to this type\n    Type : string  // 'text', 'password', 'email', 'number', 'url', 'tel', 'date', `time`, 'range', 'color'\n\n    Id : string\n    Name : string\n    Required : string\n    Description : string\n    Multiple : bool\n}\n\nclass CreateFormRepresentation : FormRepresentation\nclass EditFormRepresentation : FormRepresentation\nclass SearchFormRepresentation : FormRepresentation\n\nclass CheckInputFormItemRepresentation : FormItemRepresentation\nclass DateInputFormItemRepresentation : FormItemRepresentation\nclass DateTimeInputFormItemRepresentation : FormItemRepresentation\nclass EmailInputFormItemRepresentation : FormItemRepresentation\nclass PasswordInputFormItemRepresentation : FormItemRepresentation\nclass SelectOptionItemRepresentation : FormItemRepresentation\nclass TextInputFormItemRepresentation : FormItemRepresentation\nclass UriInputFormItemRepresentation : FormItemRepresentation\nclass CollectionInputFormItemRepresentation : FormItemRepresentation\nclass JsonPointerInputFormItemRepresentation : FormItemRepresentation\n\n\nclass GroupFormItemRepresentation : FormItemRepresentation {\n    Items: FormItemRepresentation[]\n}\nclass SelectFormItemRepresentation : FormItemRepresentation{\n    Items: SelectOptionItemRepresentation[]\n}\nclass SelectOptionItemRepresentation : FormItemRepresentation {\n    Selected: bool\n}\nclass SelectOptionGroupItemRepresentation : SelectOptionItemRepresentation {\n    Link: WebLink\n    Items: SelectOptionItemRepresentation[]\n}\nclass SelectOptionValueItemRepresentation  : SelectOptionItemRepresentation{\n    Value: string\n    Label: string\n}</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Psuedo code example construction of a (create) form</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-none\"   nocopy=\"undefined\"><code>CreateFormRepresentation\n    {\n        Links = []\n        {\n            // self link to this form\n            url.MakeTodoCreateFormUri().MakeWebLink(IanaLinkRelation.Self)\n        }\n        Items = FormItemRepresentation[]\n            {\n                TextInputFormItemRepresentation\n                {\n                    Name = \"name\"\n                    Description = \"The title of the page\"\n                    Required = true\n                }\n                SelectFormItemRepresentation\n                {\n                    Name = \"state\"\n                    Description = \"A todo can only toggle between open and complete.\"\n                    Required = false\n                    Multiple = false\n                    Items = SelectOptionItemRepresentation[]\n                    {\n                        SelectOptionValueItemRepresentation\n                        {\n                            Type = FormType.Enum\n                            Description = \"The todo has been completed\"\n                            Label = \"Completed\"\n                            Value = TodoState.Complete\n                            Name = \"completed\"\n                        }\n                        SelectOptionValueItemRepresentation\n                        {\n                            Type = FormType.Enum\n                            Description = \"The todo has been opened\"\n                            Label = \"Open\"\n                            Value = TodoState.Open\n                            Name = \"open\"\n                        }\n                    }\n                }\n                DateTimeInputFormItemRepresentation\n                {\n                    Name = \"due\"\n                    Description = \"The UTC date the todo is due\"\n                }\n            }\n    }</code></pre>\n      </div>\n</Instruction>\n<h3>Forms submission</h3>\n<p>Forms are submitted semantically. That is, there are a series of rules/conventions that allow the client to converge on a decision of where and how to submit. Some microformat require explicit rules. An alternative way to look at the problem is to see that (those explicit) rules are merely higher priority and that the client needs a range of options to fallback on. Some of this is because of the design to separate out forms from the originating resource.</p>\n<p>Key designs:</p>\n<ul>\n<li>forms are linked from the resource and thus have their own identity</li>\n<li>forms are ideally highly cachable thus by default provide no submission information</li>\n<li>forms are submitted in the context of the orginating resource</li>\n<li>forms can provide override behaviours</li>\n</ul>\n<p>Conventions because we are modelling in collections which all submit back on the ‘self’ link on originating resource:</p>\n<ul>\n<li>edit-form is an update of a singleton: PUT</li>\n<li>create-form is a new resource on a collection: POST</li>\n<li>edit-form with type ‘text/uri-list’ is an update on items on a collection: PUT</li>\n<li>edit-form with type ‘application/json-patch+json’ is an update on items in a collection: PATCH</li>\n</ul>\n<blockquote>\n<p>Note: this is a highly constrained implementation. Also, DELETE is assumed to never use forms and to DELETE items on a collection is DELETE a singleton which also removes it from the collection. A PUT/PATCH only updates the collection membership.</p>\n</blockquote>\n<p>The form may be marked up with overrides in the ‘submit’ link relation:</p>\n<ul>\n<li>HRef is an explicit resource to submit to</li>\n<li>Type is an explicit mime-type to submit using</li>\n<li>Method is an explicit override (POST, PUT, PATCH)</li>\n</ul>",
				"fields": {
					"slug": "/advanced/2-forms-modelling/"
				},
				"excerpt": "Hypermedia benefits from affordances. These are hints from the server about what the client is able to ask the server to do. The most...",
				"frontmatter": {
					"title": "Forms Modelling",
					"videoId": null,
					"videoAuthor": null,
					"parent": "/Users/todd/src/howtohypermedia/content/hypermedia/advanced/2-forms-modelling.md absPath of file",
					"question": "Why do forms work as affordances to hypermedia?",
					"answers": [
						"A form tells the client how to process properties on an in-memory resource",
						"The client has control of what is sent back to the server",
						"It's a synonym for HTML",
						"Trick question: none of this makes sense"
					],
					"correctAnswer": 3,
					"description": "Links and Forms are used to update resources and controlled by the server that the client then uses",
					"pageTitle": "Hypermedia requires forms"
				}
			},
			"mds": {
				"edges": [
					{
						"node": {
							"frontmatter": {
								"title": "Get started with your favorite technology",
								"description": null
							},
							"fields": {
								"slug": "/choose/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Update Collection Resource",
								"description": "Learn the code mechanics of a edit forms on a collection resource that uses different mime-types"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/11-update-collection/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "It was great having you!",
								"description": null
							},
							"fields": {
								"slug": "/success/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Update Singleton Resource",
								"description": "Learn the code mechanics of a edit forms on a singleton resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/10-update-singleton/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a hypermedia API that works within the constraints of REST"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting Started",
								"description": "Learn how to build a hypermedia API "
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "",
								"description": null
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/2-modelling-representations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Modelling Domain",
								"description": "Learn how to diagram the API textually and get also get visual output"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/3-modelling-domain-link-relations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Representations",
								"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/4-constructing-representations/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Collections",
								"description": "Learn the code mechanics of a singleton resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/6-constructing-collection/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Searchable Resources",
								"description": "Learn the code mechanics of a search forms on a collection"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/8-constructing-search-resources/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Singletons",
								"description": "Learn the code mechanics of a singleton resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/5-constructing-singleton/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Create Resource",
								"description": "Learn the code mechanics of a create forms on a collection"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/9-create-resource/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Constructing Virtual Resource",
								"description": "Learn the code mechanics of a virtual resource with the user resource"
							},
							"fields": {
								"slug": "/aspnetcore-todo-api/7-constructing-viritual/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a todo app and admin functions over the top of a hypermedia API"
							},
							"fields": {
								"slug": "/vue-todo-app/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Client-side URLs and routing",
								"description": "Design your application around the holding and reconstructing client state through the URI in the browser location requires matching client views and the data it requires"
							},
							"fields": {
								"slug": "/vue-todo-app/2-client-side-urls-and-routing/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting started",
								"description": "Learn how to build a todo client to use the hypermedia API "
							},
							"fields": {
								"slug": "/vue-todo-app/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Client-side application cache",
								"description": "Client need to have one copy of representations in the application cache"
							},
							"fields": {
								"slug": "/vue-todo-app/3-application-cache/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Updates and deletes",
								"description": "The application cache also deals with the updates and deletion of resources"
							},
							"fields": {
								"slug": "/vue-todo-app/4-updates-and-deletes/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "On-demand Authentication",
								"description": "Be able to create and renew authentication tokens when the server requests valid authentication tokens"
							},
							"fields": {
								"slug": "/vue-todo-app/5-on-demand-authentication/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Summary",
								"description": "Learn how to build a todo app over the top of a hypermedia API"
							},
							"fields": {
								"slug": "/vue-todo-app/7-summary/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Getting Started",
								"description": "Learn how to build a general purpose client to browse over the API "
							},
							"fields": {
								"slug": "/vue-api-browser/1-getting-started/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Cloning Resources",
								"description": "Forms in hypermedia make cloning of trees straightforward and we can leverage that with drag and drop functionality alongside the client-side application cache"
							},
							"fields": {
								"slug": "/vue-todo-app/6-cloning-resources/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "HTML representation",
								"description": "Learn what and why to build HTML representation of data with code on demand"
							},
							"fields": {
								"slug": "/vue-api-browser/2-build-html-representation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn how to build a general purpose client to browse over the API "
							},
							"fields": {
								"slug": "/vue-api-browser/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Generate HTML",
								"description": "Learn how to respond as HTML in ASP.NET Core"
							},
							"fields": {
								"slug": "/vue-api-browser/3-build-html-content-negotiation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Register text/html",
								"description": "Learn how to respond to HTML content negotiation in ASP.NET Core"
							},
							"fields": {
								"slug": "/vue-api-browser/4-register-html-content-negotiation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "GET application/json",
								"description": "Learn how to request JSON from the client"
							},
							"fields": {
								"slug": "/vue-api-browser/5-request-json/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Summary",
								"description": "Learn how to user hypermedia as engine to navigate an API and make changes"
							},
							"fields": {
								"slug": "/vue-api-browser/10-summary/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Displaying with interaction",
								"description": "Learn how to process JSON to make delete, create and update"
							},
							"fields": {
								"slug": "/vue-api-browser/6-displaying-with-interaction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Displaying forms",
								"description": "Learn how to process form values for create and edit forms"
							},
							"fields": {
								"slug": "/vue-api-browser/8-displaying-form-values/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Deleting a resource",
								"description": "Learn how to process JSON to try and delete a resource"
							},
							"fields": {
								"slug": "/vue-api-browser/7-try-delete-representation/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Introduction",
								"description": "Learn all about hypermedia and why a REST-style of architecture is simple and subtle. It's not really for the lazy or those who like to be clever."
							},
							"fields": {
								"slug": "/basics/0-introduction/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Minimally layered design",
								"description": "Learn why hypermedia is a thin layer across persistence that should guide us through the business problem."
							},
							"fields": {
								"slug": "/basics/1-minimally-layered-design/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Submitting forms",
								"description": "Learn how to process creates (POST) and updates (PUT) on a resource"
							},
							"fields": {
								"slug": "/vue-api-browser/9-submitting-forms/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Big Picture (Trends)",
								"description": "Learn that distributed applications have trended towards data and business logic being processed at the client edge"
							},
							"fields": {
								"slug": "/basics/3-big-picture/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Hypermedia for the dedicated",
								"description": "Learn some techniques that will ease the server and client development"
							},
							"fields": {
								"slug": "/basics/4-for-the-dedicated/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Navigating a hypermedia API",
								"description": "Learn what a hypermedia looks like to be able to explore a domain."
							},
							"fields": {
								"slug": "/basics/2-navigating-api/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Multiple clients",
								"description": "Learn that most APIs should have multiple types of clients."
							},
							"fields": {
								"slug": "/advanced/0-multiple-clients/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "API Modelling",
								"description": "Learn about how collections and singletons are modelled in hypermedia"
							},
							"fields": {
								"slug": "/advanced/1-api-modelling/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Forms Modelling",
								"description": "Links and Forms are used to update resources and controlled by the server that the client then uses"
							},
							"fields": {
								"slug": "/advanced/2-forms-modelling/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Common Questions",
								"description": "Answer common questions about these tutorials"
							},
							"fields": {
								"slug": "/advanced/4-common-questions/"
							}
						}
					},
					{
						"node": {
							"frontmatter": {
								"title": "Todo Domain",
								"description": "Learn about domain of the API"
							},
							"fields": {
								"slug": "/advanced/3-todo-domain/"
							}
						}
					}
				]
			}
		},
		"pathContext": {
			"slug": "/advanced/2-forms-modelling/"
		}
	};

/***/ })

});


// WEBPACK FOOTER //
// path---advanced-2-forms-modelling-3d0249fc180729f584bb.js